import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formsApi, patientApi } from '../../lib/api';
import api from '../../lib/api';
import { toPng } from 'html-to-image';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Loader2, Clock, History, Printer, Copy, Send, PenTool,
  FileDown, Edit, Eye, Trash2, Image as ImageIcon,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { evaluateFormula, formatComputed } from '../../lib/formCalc';
import CalcTableRenderer from '../../components/forms/CalcTableRenderer';
import FormFilledView from '../../components/forms/FormFilledView';
import PrintPatientHeader from '../../components/forms/PrintPatientHeader';
import PrintPreviewDialog from '../../components/forms/PrintPreviewDialog';
import SignaturePadDialog from '../../components/forms/SignaturePadDialog';
import FillBlankField from '../../components/forms/FillBlankField';

export default function FormSubmissionEditPage() {
  const { formId, submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [showESign, setShowESign] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [showDiscard, setShowDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [signingFieldId, setSigningFieldId] = useState(null);
  const [savingImage, setSavingImage] = useState(false);

  const reload = async () => {
    const res = await formsApi.getSubmission(submissionId);
    setSubmission(res.data);
    setResponses(res.data.responses || {});
    return res.data;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await reload();
        // If this is a draft, default to edit mode so the user can resume.
        if (data?.is_draft) setMode('edit');
        // Load patient info
        if (data.patient_id) {
          try {
            const patRes = await patientApi.get(data.patient_id);
            setPatient(patRes.data);
          } catch { /* ignore */ }
        }
        // Load the source form to know print_layout (for "Print as Original" button)
        if (formId) {
          try {
            const fRes = await formsApi.get(formId);
            setForm(fRes.data);
          } catch { /* ignore */ }
        }
        // Load org branding (current user's own org — works for all roles)
        try {
          const orgRes = await api.get('/organizations/me');
          if (orgRes.data) setOrg(orgRes.data);
        } catch { /* ignore */ }
      } catch { toast.error('Failed to load submission'); navigate(-1); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  // Auto-compute `calculated` fields whenever responses change (edit mode only)
  useEffect(() => {
    if (mode !== 'edit') return;
    const calcFields = (form?.fields || []).filter(f => f.field_type === 'calculated' && f.formula);
    if (calcFields.length === 0) return;
    const updates = {};
    for (const cf of calcFields) {
      const result = evaluateFormula(cf.formula, { fields: responses });
      if (result !== null && responses[cf.field_id] !== result) {
        updates[cf.field_id] = result;
      }
    }
    if (Object.keys(updates).length > 0) {
      setResponses(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, form, mode]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await formsApi.updateSubmission(submissionId, { responses });
      toast.success('Submission updated');
      await reload();
      setMode('view');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleCopy = async () => {
    try {
      await formsApi.submit(formId, {
        form_id: formId,
        patient_id: submission.patient_id,
        responses: { ...responses }
      });
      toast.success('Form copied as new submission');
      navigate(-1);
    } catch { toast.error('Failed to copy form'); }
  };

  const handleDiscard = async () => {
    setDiscarding(true);
    try {
      // Drafts: use draft endpoint (cleaner audit trail). Saved: use submissions endpoint.
      if (submission?.is_draft) {
        await api.delete(`/form-drafts/${submissionId}`);
        toast.success('Draft discarded');
      } else {
        await formsApi.deleteSubmission(submissionId);
        toast.success('Submission deleted');
      }
      setShowDiscard(false);
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to discard');
    } finally { setDiscarding(false); }
  };

  // Capture the form submission as a PNG and upload it to the patient's files.
  // Uses html-to-image to rasterise the submission-edit-page container, then
  // posts the data URL to POST /patients/{id}/files/upload where it gets
  // stored as a patient_file_upload with source='form_snapshot'.
  const handleSaveAsImage = async () => {
    if (!submission?.patient_id) {
      toast.error('This submission is not linked to a patient');
      return;
    }
    setSavingImage(true);
    try {
      const node = document.querySelector('[data-testid="submission-edit-page"]');
      if (!node) throw new Error('Could not find submission content');
      // Temporarily add submission-print-mode so the screen chrome hides and
      // the PDF-style layout renders, matching what the user printed.
      document.body.classList.add('submission-print-mode');
      await new Promise(r => setTimeout(r, 300));
      const dataUrl = await toPng(node, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        cacheBust: true,
        // Skip dialog portals, the running-header, and anything with .no-print
        filter: (n) => {
          if (!n.classList) return true;
          if (n.classList.contains('no-print')) return false;
          if (n.hasAttribute && n.hasAttribute('data-radix-portal')) return false;
          return true;
        },
      });
      document.body.classList.remove('submission-print-mode');
      const patientSlug = (patient ? `${patient.first_name}_${patient.last_name}` : 'patient').replace(/\s+/g, '_');
      const formSlug = (submission.form_name || 'form').replace(/[^a-z0-9]+/gi, '_');
      const filename = `${formSlug}__${patientSlug}__${submissionId.slice(-6)}.png`;
      await patientApi.uploadFile(submission.patient_id, {
        filename,
        mime_type: 'image/png',
        data_url: dataUrl,
        note: `Form snapshot: ${submission.form_name} (submission ${submissionId})`,
        source: 'form_snapshot',
      });
      toast.success('Saved to Patient Files');
    } catch (e) {
      document.body.classList.remove('submission-print-mode');
      const detail = e?.response?.data?.detail || e?.message || 'Failed to save image';
      toast.error(detail);
    } finally {
      setSavingImage(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;
  if (!submission) return null;

  const fields = submission.form_fields || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="submission-edit-page">
      {/* Running print header — pinned to every page via @page CSS rules.
          Repeats the FULL patient overview on top of every printed page. */}
      <PrintPatientHeader patient={patient} form={submission.form_name} />

      {/* Action Bar - hidden on print */}
      <div className="flex items-center gap-3 no-print">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {submission.form_name}
            {submission.is_draft && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] align-middle">DRAFT</span>}
          </h1>
          <div className="flex items-center gap-3 text-sm text-[#64748B]">
            <span>{submission.is_draft ? 'Last saved: ' : 'Submitted: '}{new Date(submission.updated_at || submission.submitted_at).toLocaleString()}</span>
            {submission.submitted_by_name && <span>By: {submission.submitted_by_name}</span>}
            {submission.aide_name && <span data-testid="submission-aide">Aide: {submission.aide_name}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {mode === 'view' ? (
            <Button variant="outline" onClick={() => setMode('edit')} data-testid="enter-edit-mode-btn">
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
          ) : (
            <Button variant="outline" onClick={() => { setMode('view'); setResponses(submission.responses || {}); }} data-testid="exit-edit-mode-btn">
              <Eye className="w-4 h-4 mr-2" />View
            </Button>
          )}
          <Button variant="outline" onClick={handleCopy} data-testid="copy-submission-btn">
            <Copy className="w-4 h-4 mr-2" />Copy as New
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // If the form was authored with a PDF-original print layout,
              // skip the generic preview and open the dedicated original-layout
              // print page in a new tab — that view overlays the filled values
              // on top of the source PDF pages, which is exactly what the user
              // wants for government / CMS-style forms (CMS-485, etc.).
              if (form?.print_layout === 'pdf_original') {
                window.open(`/forms/${formId}/print-original?submissionId=${submissionId}`, '_blank');
              } else {
                setShowPrintPreview(true);
              }
            }}
            data-testid="print-submission-btn"
          >
            <Printer className="w-4 h-4 mr-2" />Print
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const res = await formsApi.downloadSubmissionPdf(submissionId);
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const safeName = (submission.form_name || 'form').replace(/[^a-z0-9]+/gi, '_');
                a.download = `${safeName}_${submissionId}.pdf`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                toast.success('PDF downloaded');
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Failed to download PDF');
              }
            }}
            data-testid="download-pdf-btn"
            className="border-[#0E7490] text-[#0E7490]"
            title="Download a clean PDF of the filled form"
          >
            <FileDown className="w-4 h-4 mr-2" />Download PDF
          </Button>
          {submission.patient_id && (
            <Button
              variant="outline"
              onClick={handleSaveAsImage}
              disabled={savingImage}
              data-testid="save-image-to-files-btn"
              className="border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F3FF]"
              title="Save this form as a PNG image into the patient's files"
            >
              {savingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
              Save to Files
            </Button>
          )}
          {(submission?.e_signers || []).length > 0 && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await api.get(`/form-submissions/${submissionId}/audit-pdf`, { responseType: 'blob' });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `submission_${submissionId}_audit.pdf`;
                  document.body.appendChild(a); a.click();
                  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to export PDF');
                }
              }}
              data-testid="export-audit-pdf-btn"
              className="border-[#166534] text-[#166534]"
              title="Download signed PDF with full HIPAA audit trail"
            >
              <FileDown className="w-4 h-4 mr-2" />Export Signed PDF
            </Button>
          )}
          {form?.print_layout === 'pdf_original' && (
            <Button
              variant="outline"
              onClick={() => window.open(`/forms/${formId}/print-original?submissionId=${submissionId}`, '_blank')}
              data-testid="print-original-layout-btn"
              className="border-[#0E7490] text-[#0E7490]"
              title="Print using the original PDF layout"
            >
              <Printer className="w-4 h-4 mr-2" />Print as Original Layout
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowESign(true)} data-testid="send-e-sign-btn" className="border-[#0E7490] text-[#0E7490]">
            <Send className="w-4 h-4 mr-2" />Send for e-Signature
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDiscard(true)}
            data-testid="discard-submission-btn"
            className="border-[#991B1B] text-[#991B1B] hover:bg-[#FEE2E2]"
            title={submission.is_draft ? 'Delete this unfinished draft' : 'Delete this submission'}
          >
            <Trash2 className="w-4 h-4 mr-2" />{submission.is_draft ? 'Discard Draft' : 'Discard'}
          </Button>
          {mode === 'edit' && (
            <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-submission-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save Changes
            </Button>
          )}
        </div>
      </div>

      <ESignDialog
        open={showESign}
        onClose={() => setShowESign(false)}
        form={form}
        submissionId={submissionId}
      />

      <DiscardDialog
        open={showDiscard}
        onClose={() => setShowDiscard(false)}
        onConfirm={handleDiscard}
        loading={discarding}
        isDraft={!!submission.is_draft}
      />

      <PrintPreviewDialog
        open={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        contentSelector='[data-testid="submission-edit-page"]'
        bodyClass="submission-print-mode"
        title={`Print Preview — ${submission.form_name}`}
      />

      <SignaturePadDialog
        open={!!signingFieldId}
        onClose={() => setSigningFieldId(null)}
        onSave={(dataUrl) => {
          if (signingFieldId) {
            updateResponse(signingFieldId, dataUrl);
            toast.success('Signature captured');
          }
          setSigningFieldId(null);
        }}
        label={(submission?.form_fields || []).find(f => f.field_id === signingFieldId)?.label || 'Signature'}
        initialValue={signingFieldId && typeof responses[signingFieldId] === 'string' && responses[signingFieldId].startsWith('data:image') ? responses[signingFieldId] : null}
      />

      {/* Print Header - only visible in print */}
      <div className="hidden print-show" data-testid="print-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #0E7490', paddingBottom: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {org?.logo_url && <img src={org.logo_url} alt="Logo" style={{ height: '48px', objectFit: 'contain' }} />}
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0A2342' }}>{org?.display_name || org?.name || ''}</div>
              {org?.phone && <div style={{ fontSize: '12px', color: '#64748B' }}>{org.phone}</div>}
              {org?.address && <div style={{ fontSize: '12px', color: '#64748B' }}>{org.address}</div>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0A2342' }}>{submission.form_name}</div>
            <div style={{ fontSize: '11px', color: '#64748B' }}>Submitted: {new Date(submission.submitted_at).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Patient Information - always visible, prominent on print */}
      {patient && (
        <Card className="careiq-card border-l-4 border-l-[#0E7490]" data-testid="patient-info-section">
          <CardContent className="p-4">
            <h3 className="font-semibold text-[#0A2342] text-sm mb-2">Patient Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
              <div><span className="text-[#64748B]">Name:</span> <strong>{patient.first_name} {patient.last_name}</strong></div>
              <div><span className="text-[#64748B]">DOB:</span> <strong>{patient.date_of_birth}</strong></div>
              <div><span className="text-[#64748B]">MRN:</span> <strong>{patient.mrn}</strong></div>
              <div><span className="text-[#64748B]">Gender:</span> <strong>{patient.gender || '—'}</strong></div>
              <div><span className="text-[#64748B]">Phone:</span> <strong>{patient.phone || '—'}</strong></div>
              <div><span className="text-[#64748B]">Email:</span> <strong>{patient.email || '—'}</strong></div>
              <div><span className="text-[#64748B]">Insurance:</span> <strong>{patient.insurance_provider || '—'}</strong></div>
              <div><span className="text-[#64748B]">Insurance ID:</span> <strong>{patient.insurance_id || '—'}</strong></div>
              <div><span className="text-[#64748B]">Address:</span> <strong>{patient.address || '—'}</strong></div>
              <div><span className="text-[#64748B]">Language:</span> <strong>{patient.primary_language || '—'}</strong></div>
              <div><span className="text-[#64748B]">Emergency Contact:</span> <strong>{patient.emergency_contact_name || '—'}</strong></div>
              <div><span className="text-[#64748B]">Emergency Phone:</span> <strong>{patient.emergency_contact_phone || '—'}</strong></div>
              <div><span className="text-[#64748B]">Emergency Relation:</span> <strong>{patient.emergency_contact_relationship || '—'}</strong></div>
              <div><span className="text-[#64748B]">Practitioner:</span> <strong>{patient.medical_practitioner || '—'}</strong></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Trail - hidden on print */}
      {submission.edit_history?.length > 0 && (
        <Card className="careiq-card border-l-4 border-l-[#CA8A04] no-print">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-[#92400E] mb-2"><History className="w-4 h-4" />Edit History</div>
            <div className="space-y-1">
              {submission.edit_history.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-[#64748B]">
                  <Clock className="w-3 h-3" />
                  <span className="font-medium">{h.edited_by_name || h.edited_by_role}</span>
                  <span>edited on {new Date(h.edited_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ---- View Mode: identical layout to the original blank form, with values filled in ---- */}
      {mode === 'view' && (
        <FormFilledView submission={submission} form={form} />
      )}

      {/* ---- Edit Mode: editable fields ---- */}
      {mode === 'edit' && (
        <div className="flex flex-wrap gap-3">
          {fields.sort((a, b) => a.order - b.order).map((field) => {
            const widthClass = field.width === 'half' ? 'w-full md:w-[calc(50%-6px)]' : field.width === 'third' ? 'w-full md:w-[calc(33.333%-8px)]' : 'w-full';

            if (field.field_type === 'section') {
              return <div key={field.field_id} className="w-full border-b-2 border-[#0E7490] pt-4 pb-1"><h3 className="text-sm font-bold text-[#0E7490]">{field.label}</h3></div>;
            }
            if (field.field_type === 'info') {
              return <div key={field.field_id} className={`${widthClass} p-3 bg-[#F0FDFA] border border-[#14B8A6]/20 rounded-lg text-sm text-[#0A2342]`}>{field.info_text || field.label}</div>;
            }
            if (field.field_type === 'reminder') return null;

            if (field.field_type === 'calculated') {
              return (
                <div key={field.field_id} className={widthClass} data-testid={`edit-calc-${field.field_id}`}>
                  <Label className="font-semibold text-[#0A2342]">{field.label}</Label>
                  <div className="mt-1 px-3 py-2 bg-[#EFF6FF] border border-[#2563EB]/20 rounded-lg font-mono text-base text-[#1E40AF]">
                    {formatComputed(evaluateFormula(field.formula || '', { fields: responses }), field.format || { type: 'number', decimals: 2 })}
                    <span className="text-[10px] text-[#64748B] ml-2">(auto-calculated)</span>
                  </div>
                </div>
              );
            }
            if (field.field_type === 'calc_table') {
              return (
                <div key={field.field_id} className={widthClass} data-testid={`edit-calctable-${field.field_id}`}>
                  <CalcTableRenderer field={field} value={responses[field.field_id]} onChange={(v) => updateResponse(field.field_id, v)} outerFields={responses} />
                </div>
              );
            }

            if (field.field_type === 'multi_column') {
              const val = responses[field.field_id] || {};
              return (
                <Card key={field.field_id} className={`careiq-card ${widthClass}`} data-testid={`edit-field-${field.field_id}`}>
                  <CardContent className="p-4">
                    <Label className="font-semibold text-[#0A2342] mb-2 block">{field.label}</Label>
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${field.column_count || 2}, 1fr)` }}>
                      {(field.children || []).map((col, colIdx) => (
                        <div key={colIdx} className="space-y-2">
                          {(col || []).map((child, childIdx) => {
                            const childKey = `${field.field_id}_${colIdx}_${childIdx}`;
                            const childVal = val[childKey];
                            return (
                              <div key={childIdx} className="space-y-1">
                                {child.label && <Label className="text-xs text-[#64748B]">{child.label}</Label>}
                                {(child.field_type === 'text' || child.field_type === 'number' || child.field_type === 'email' || child.field_type === 'phone') && (
                                  <Input type={child.field_type === 'number' ? 'number' : 'text'} value={childVal || ''} onChange={(e) => updateResponse(field.field_id, { ...val, [childKey]: e.target.value })} />
                                )}
                                {child.field_type === 'date' && <Input type="date" value={childVal || ''} onChange={(e) => updateResponse(field.field_id, { ...val, [childKey]: e.target.value })} />}
                                {child.field_type === 'textarea' && <Textarea value={childVal || ''} onChange={(e) => updateResponse(field.field_id, { ...val, [childKey]: e.target.value })} className="min-h-[60px]" />}
                                {child.field_type === 'select' && (
                                  <Select value={childVal || ''} onValueChange={(v) => updateResponse(field.field_id, { ...val, [childKey]: v })}>
                                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                                    <SelectContent>{(child.options || []).map((o, oi) => <SelectItem key={oi} value={String(o)}>{String(o)}</SelectItem>)}</SelectContent>
                                  </Select>
                                )}
                                {child.field_type === 'checkbox' && (
                                  <div className="space-y-1">{(child.options || []).map((opt, oi) => (
                                    <label key={oi} className="flex items-center gap-2 cursor-pointer text-sm">
                                      <input type="checkbox" checked={(childVal || []).includes(String(opt))} onChange={() => {
                                        const arr = [...(childVal || [])];
                                        const idx = arr.indexOf(String(opt));
                                        if (idx >= 0) arr.splice(idx, 1); else arr.push(String(opt));
                                        updateResponse(field.field_id, { ...val, [childKey]: arr });
                                      }} className="rounded" />
                                      {String(opt)}
                                    </label>
                                  ))}</div>
                                )}
                                {child.field_type === 'radio' && (
                                  <div className="space-y-1">{(child.options || []).map((opt, oi) => (
                                    <label key={oi} className="flex items-center gap-2 cursor-pointer text-sm">
                                      <input type="radio" name={childKey} checked={childVal === String(opt)} onChange={() => updateResponse(field.field_id, { ...val, [childKey]: String(opt) })} />
                                      {String(opt)}
                                    </label>
                                  ))}</div>
                                )}
                                {child.field_type === 'info' && <div className="p-2 bg-[#F0FDFA] rounded text-xs">{child.info_text || child.label}</div>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            if (field.field_type === 'checkbox_table') {
              const val = responses[field.field_id] || {};
              return (
                <Card key={field.field_id} className={`careiq-card ${widthClass}`} data-testid={`edit-field-${field.field_id}`}>
                  <CardContent className="p-4">
                    <Label className="font-semibold mb-2 block">{field.label}</Label>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-[#E2E8F0] rounded-lg">
                        <thead><tr className="bg-[#F1F5F9]">
                          {(field.table_columns || []).map((col, ci) => <th key={ci} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{col}</th>)}
                        </tr></thead>
                        <tbody>
                          {(field.table_rows || []).map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E8F0]">
                              <td className="p-2 text-xs font-medium">{row}</td>
                              {(field.table_columns || []).slice(1).map((col, ci) => (
                                <td key={ci} className="p-2 text-center">
                                  <input type="checkbox" checked={!!val[`${ri}_${ci}`]} onChange={(e) => updateResponse(field.field_id, { ...val, [`${ri}_${ci}`]: e.target.checked })} className="rounded" />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            }

            // Standard fields
            return (
              <Card key={field.field_id} className={`careiq-card ${widthClass}`} data-testid={`edit-field-${field.field_id}`}>
                <CardContent className="p-4">
                  <Label className="flex items-center gap-1 mb-2">
                    {field.label}
                    {field.required && <span className="text-[#DC2626]">*</span>}
                  </Label>
                  {(field.field_type === 'text' || field.field_type === 'number' || field.field_type === 'email' || field.field_type === 'phone') && (
                    <Input type={field.field_type === 'number' ? 'number' : 'text'} value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} />
                  )}
                  {field.field_type === 'date' && <Input type="date" value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} />}
                  {field.field_type === 'textarea' && <Textarea value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} className="min-h-[80px]" />}
                  {field.field_type === 'select' && (
                    <Select value={responses[field.field_id] || ''} onValueChange={(v) => updateResponse(field.field_id, v)}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>{(field.options || []).map((opt, i) => <SelectItem key={i} value={String(opt)}>{String(opt)}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {field.field_type === 'radio' && (
                    <div className="space-y-2">{(field.options || []).map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="radio" name={field.field_id} checked={responses[field.field_id] === String(opt)} onChange={() => updateResponse(field.field_id, String(opt))} />
                        {String(opt)}
                      </label>
                    ))}</div>
                  )}
                  {field.field_type === 'checkbox' && (
                    <div className="space-y-2">{(field.options || []).map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={(responses[field.field_id] || []).includes(String(opt))} onChange={() => {
                          const cur = responses[field.field_id] || [];
                          updateResponse(field.field_id, cur.includes(String(opt)) ? cur.filter(o => o !== String(opt)) : [...cur, String(opt)]);
                        }} className="rounded" />
                        {String(opt)}
                      </label>
                    ))}</div>
                  )}
                  {field.field_type === 'checkbox_text' && (
                    <div className="space-y-2">{(field.options || []).map((opt, i) => {
                      const val = responses[field.field_id] || {};
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <input type="checkbox" checked={!!val[`chk_${i}`]} onChange={(e) => updateResponse(field.field_id, { ...val, [`chk_${i}`]: e.target.checked })} className="rounded shrink-0" />
                          <span className="text-sm shrink-0 min-w-[80px]">{String(opt)}</span>
                          <Input value={val[`txt_${i}`] || ''} onChange={(e) => updateResponse(field.field_id, { ...val, [`txt_${i}`]: e.target.value })} className="h-8 text-sm flex-1" disabled={!val[`chk_${i}`]} />
                        </div>
                      );
                    })}</div>
                  )}
                  {field.field_type === 'signature' && (
                    <div
                      className="border-2 border-dashed border-[#94A3B8] rounded-lg p-3 text-center cursor-pointer hover:border-[#0E7490] transition-colors"
                      data-testid={`edit-signature-${field.field_id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSigningFieldId(field.field_id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSigningFieldId(field.field_id); } }}
                    >
                      {responses[field.field_id] && typeof responses[field.field_id] === 'string' && responses[field.field_id].startsWith('data:image') ? (
                        <>
                          <img src={responses[field.field_id]} alt={`Signature: ${field.label}`} className="max-h-24 mx-auto border border-[#E2E8F0] rounded bg-white" />
                          {(() => {
                            const signer = (submission?.e_signers || []).find(s =>
                              Array.isArray(s.signature_fields) && s.signature_fields.includes(field.field_id)
                            );
                            return signer ? (
                              <div className="text-xs text-[#166534] mt-1" data-testid={`edit-signature-meta-${field.field_id}`}>
                                Signed by <b>{signer.signer_name}</b> ({signer.signer_type}) on {new Date(signer.signed_at).toLocaleString()}
                              </div>
                            ) : null;
                          })()}
                          <div className="text-[10px] text-[#0E7490] mt-1">Tap to re-sign</div>
                        </>
                      ) : (
                        <div className="text-sm text-[#94A3B8] py-2">
                          <PenTool className="w-6 h-6 mx-auto mb-1" />
                          Tap to draw your signature
                        </div>
                      )}
                    </div>
                  )}
                  {field.field_type === 'file' && <div className="text-sm text-[#64748B]">{responses[field.field_id] || 'No file'}</div>}
                  {field.field_type === 'fill_blank' && (
                    <FillBlankField
                      template={field.fib_template || ''}
                      value={Array.isArray(responses[field.field_id]) ? responses[field.field_id] : []}
                      onChange={(arr) => updateResponse(field.field_id, arr)}
                      testIdPrefix={`fib-edit-${field.field_id}`}
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Discard confirmation dialog ----------
function DiscardDialog({ open, onClose, onConfirm, loading, isDraft }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="discard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#991B1B]">
            <Trash2 className="w-5 h-5" />
            {isDraft ? 'Discard unfinished draft?' : 'Delete submission?'}
          </DialogTitle>
          <DialogDescription className="pt-2 text-[#64748B]">
            {isDraft ? (
              <>This form hasn&apos;t been submitted yet. Discarding will permanently remove the draft and any unsaved responses. This cannot be undone.</>
            ) : (
              <>This submission has already been saved. Deleting it will permanently remove the record and all edit history. <strong className="text-[#991B1B]">This cannot be undone.</strong></>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="discard-cancel-btn">Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="bg-[#991B1B] text-white hover:bg-[#B91C1C] rounded-full px-6"
            data-testid="discard-confirm-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {isDraft ? 'Discard Draft' : 'Delete Submission'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- e-Signature request dialog ----------
function ESignDialog({ open, onClose, form, submissionId }) {
  const [signerType, setSignerType] = useState('patient');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [expiresHours, setExpiresHours] = useState(72);
  const [assignedFields, setAssignedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState([]);

  // Signature fields from the form
  const sigFields = React.useMemo(() => (form?.fields || []).filter(f => (f.field_type || f.type) === 'signature'), [form]);

  // Auto-select signature fields when signer type changes (best-effort — label-based heuristic)
  useEffect(() => {
    if (!sigFields.length) return;
    const byLabel = (needle) => sigFields.filter(f => (f.label || '').toLowerCase().includes(needle)).map(f => f.field_id || f.id);
    if (signerType === 'patient') setAssignedFields(byLabel('patient').length ? byLabel('patient') : byLabel('signature'));
    else if (signerType === 'physician') setAssignedFields(byLabel('physician').length ? byLabel('physician') : byLabel('doctor'));
    else setAssignedFields([]);
  }, [signerType, sigFields]);

  const loadExisting = React.useCallback(async () => {
    try {
      const res = await api.get('/signature-requests');
      const forThisSub = (res.data.signature_requests || []).filter(s => s.id && (s.form_name === form?.name));
      setExisting(forThisSub);
    } catch { /* ignore */ }
  }, [form?.name]);

  useEffect(() => { if (open) { setResult(null); loadExisting(); } }, [open, loadExisting]);

  const toggleField = (fid) => setAssignedFields(prev => prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);

  const handleCreate = async () => {
    if (!signerName.trim()) { toast.error('Signer name required'); return; }
    if (!assignedFields.length) { toast.error('Select at least one signature field'); return; }
    setLoading(true);
    try {
      const res = await api.post('/signature-requests', {
        submission_id: submissionId,
        signer_type: signerType,
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim() || undefined,
        signature_fields: assignedFields,
        expires_hours: Number(expiresHours) || 72,
      });
      const origin = window.location.origin;
      const fullLink = `${origin}/sign/${res.data.token}`;
      setResult({ ...res.data, full_link: fullLink });
      toast.success('Link created — copy and share with the signer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Copy failed — long-press to select the link manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="e-sign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenTool className="w-5 h-5 text-[#0E7490]" /> Send for e-Signature</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="text-xs text-[#64748B]">Generate a one-time secure link. The signer opens it on any device, reviews the form, and draws their signature.</div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="careiq-label">Signer Type</Label>
                <Select value={signerType} onValueChange={setSignerType}>
                  <SelectTrigger data-testid="signer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="patient">Patient</SelectItem>
                    <SelectItem value="physician">Physician (Doctor)</SelectItem>
                    <SelectItem value="external">Other (witness, guardian, etc.)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="careiq-label">Link Expires In (hours)</Label>
                <Input type="number" value={expiresHours} onChange={e => setExpiresHours(e.target.value)} min="1" max="720" className="careiq-input" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="careiq-label">Signer Name *</Label>
                <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Full name" className="careiq-input" data-testid="signer-name-input" />
              </div>
              <div>
                <Label className="careiq-label">Email (optional)</Label>
                <Input type="email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)} placeholder="For records only (no email sent yet)" className="careiq-input" />
              </div>
            </div>

            <div>
              <Label className="careiq-label">Signature fields this person will sign *</Label>
              {sigFields.length === 0 ? (
                <div className="p-3 rounded bg-[#FEE2E2] text-[#991B1B] text-xs">This form has no signature fields. Add signature fields in the form builder first.</div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2" data-testid="sig-field-picker">
                  {sigFields.map(f => {
                    const fid = f.field_id || f.id;
                    return (
                      <label key={fid} className="flex items-center gap-2 p-1 hover:bg-[#F8FAFC] rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={assignedFields.includes(fid)}
                          onChange={() => toggleField(fid)}
                          data-testid={`sig-field-${fid}`}
                        />
                        <span className="text-sm">{f.label}</span>
                        {f.page_index !== undefined && <span className="text-[10px] text-[#94A3B8]">· page {f.page_index + 1}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {existing.length > 0 && (
              <details className="text-xs text-[#64748B] border rounded p-2">
                <summary className="cursor-pointer font-medium">Existing signature links for this form ({existing.length})</summary>
                <div className="mt-2 space-y-1">
                  {existing.slice(0, 8).map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-1 hover:bg-[#F8FAFC] rounded">
                      <span className="truncate"><span className="font-medium">{s.doctor_name || 'Signer'}</span> — <span className={s.status === 'signed' ? 'text-[#166534]' : 'text-[#CA8A04]'}>{s.status}</span></span>
                      <button className="text-[#0E7490] hover:underline" onClick={() => copyLink(`${window.location.origin}${s.sign_link}`)}>Copy link</button>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleCreate} disabled={loading} data-testid="create-e-sign-link-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Create Signing Link
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-[#F0FDFA] border border-[#A7F3D0]">
              <div className="text-sm font-semibold text-[#065F46] mb-1">✓ Signing link created</div>
              <div className="text-xs text-[#065F46] mb-3">Copy the link below and send to <strong>{result.signer_name}</strong>. Expires {new Date(result.expires_at).toLocaleString()}.</div>
              <div className="flex items-center gap-2 bg-white rounded p-2 border">
                <Input value={result.full_link} readOnly className="careiq-input flex-1 text-xs font-mono" data-testid="e-sign-link-value" />
                <Button size="sm" onClick={() => copyLink(result.full_link)} className="careiq-btn-primary" data-testid="copy-e-sign-link-btn">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResult(null); loadExisting(); }}>Create Another</Button>
              <Button className="careiq-btn-primary" onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
